'use client'

/* ---------------------------------
TestimonialSlider
--------------------------------- */

import React, { ReactElement } from 'react'
import {
  Anchor,
  OpeningTitle,
  Paragraph,
  Section,
  TestimonialCard,
  Title,
  VideoCard,
} from '@dynamo-caffeina-dev/airc-ui-kit'
import Link from 'next/link'
import DOMPurify from 'isomorphic-dompurify'
import { SectionBackground, TestimonialSliderSlideType } from '@/types'
import { TestimonialSliderModel } from '@/models/TestimonialSlider'
import Slider from '@/components/molecules/Slider'
import { handleOnClickModel } from '@/helpers/models'

type OwnProps = {
  props: ReturnType<typeof TestimonialSliderModel>['props']
  modelledContent: ReturnType<typeof TestimonialSliderModel>['modelledContent']
}

export default function TestimonialSlider({
  props,
  modelledContent: extractedContent,
}: OwnProps): ReactElement {
  const {
    openingTitle,
    title: _title,
    paragraph: _paragraph,
    cta,
    slides,
  } = extractedContent ?? {}

  const { direction, distribution, bg } = props ?? {}
  const { content: titleContent, ...title } = _title
  const { content: paragraphContent, ...paragraph } = _paragraph

  return (
    <Section
      bg={bg as SectionBackground | undefined}
      className={`section-testimonial-slider`}
      data-direction={direction ?? 'row'}
      data-distribution={distribution ?? 'text-image'}
    >
      <div className='section-testimonial-slider__grid'>
        {/* COL 1 */}
        <div className={`section-testimonial-slider__grid__col-1`}>
          <div className={`section-testimonial-slider__title-wrapper `}>
            {openingTitle.children ? (
              <OpeningTitle
                className={`section-testimonial-slider__opening-title`}
                {...openingTitle}
              />
            ) : null}

            {titleContent ? (
              <Title
                className={`section-testimonial-slider__title`}
                {...title}
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(titleContent),
                }}
              />
            ) : null}
          </div>

          {paragraphContent ? (
            <Paragraph
              // We override Paragraph's default alignment by following the Title's
              // alignment; this is needed because the WYSIWYG editor won't pass
              // any style for its default alignment (which is left).
              // If the user sets an alignment other than left in the WYSIWYG editor,
              // the resulting HTML with inline styles will override this alignment.
              alignment={title?.alignment}
              as={'div'}
              className={`section-testimonial-slider__paragraph`}
              // TODO sanitize?
              dangerouslySetInnerHTML={{ __html: paragraphContent }}
              {...paragraph}
            />
          ) : null}

          {cta?.label && cta?.href ? (
            <Anchor
              wrapper={'div'}
              data-alignment={cta.alignment}
              className={`section-testimonial-slider__cta link-button-themed`}
              as={Link}
              {...cta}
            >
              {cta.label}
            </Anchor>
          ) : null}
        </div>

        {/* COL 2 */}
        <div className='section-testimonial-slider__grid__col-2'>
          {slides?.length ? (
            <Slider
              className={'section-testimonial-slider__slider'}
              slides={slides.map(
                (slide: TestimonialSliderSlideType, i: number) => {
                  const { video, testimonial: _testimonial } = slide

                  const { onClick: testimonialOnClick, ...testimonial } =
                    _testimonial

                  return (
                    <div
                      key={'VideoCard__' + i}
                      style={{ paddingBottom: 'var(--Spacing-M)' }}
                    >
                      {/* @ts-ignore TS can't see the props due to the spread */}
                      <VideoCard
                        mbM
                        className={'section-testimonial-slider__video-card'}
                        {...{ video }}
                      />

                      <TestimonialCard
                        className={
                          'section-testimonial-slider__testimonial-card'
                        }
                        clickable={!!testimonialOnClick}
                        {...testimonial}
                        {...(testimonialOnClick
                          ? { onClick: handleOnClickModel(testimonialOnClick) }
                          : {})}
                      />
                    </div>
                  )
                }
              )}
            ></Slider>
          ) : null}
        </div>
      </div>
    </Section>
  )
}
